import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Alert,
  AlertTitle,
  Refresh,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  Info,
  Timeline,
  AccountBalance,
  Speed,
  BarChart,
  SwapHoriz,
  AutoGraph,
  MonetizationOn,
  Verified,
  Refresh as RefreshIcon,
  Person,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTraderStatistics } from '../hooks/useTraderStatistics';

const LeaderboardPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [timeFilter, setTimeFilter] = useState('monthly');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Use the custom hook to fetch trader statistics
  const { loading, error, leaderboardData, lastUpdated, refetch } = useTraderStatistics();

  // Mock leaderboard data for XM Global Percentage Growth
  const xmPercentageData = [];

  // Mock leaderboard data for Weltrade Most Lots Traded
  const weltradeLotsData = [];

  // Mock leaderboard data for Monthly Excellence
  const monthlyExcellenceData = [];

  const categories = [
    { 
      label: 'XM Global - Top 20', 
      icon: <img src="/partnersLogo-15years.svg" alt="XM Global Logo" style={{ width: '24px', height: 'auto' }} />, 
      color: '#667eea',
      prize: '',
      description: 'Most Points Accumulated Competition'
    },
    { 
      label: 'XM Global - Percentage Growth', 
      icon: <img src="/partnersLogo-15years.svg" alt="XM Global Logo" style={{ width: '24px', height: 'auto' }} />, 
      color: '#2E7D32',
      prize: '',
      description: 'Highest Percentage Returns (Top 3)'
    },
    { 
      label: 'Weltrade', 
      icon: <BarChart />, 
      color: '#1565C0',
      prize: '',
      description: 'Most Lots Accumulated'
    },
    { 
      label: 'Monthly Excellence Award', 
      icon: <MonetizationOn />, 
      color: '#E91E63',
      prize: '',
      description: 'Best Overall XM Global Performance'
    },
  ];

  const getCurrentData = () => {
    switch (selectedCategory) {
      case 0: return leaderboardData; // Real data for first tab
      case 1: return xmPercentageData;
      case 2: return weltradeLotsData;
      case 3: return monthlyExcellenceData;
      default: return leaderboardData;
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'bronze': return '#CD7F32';
      default: return 'transparent';
    }
  };

  const getBadgeIcon = (rank) => {
    if (rank <= 3) {
      return <EmojiEvents sx={{ color: getBadgeColor(rank === 1 ? 'gold' : rank === 2 ? 'silver' : 'bronze') }} />;
    }
    if (rank >= 4 && rank <= 10) {
      return <Star sx={{ color: '#9E9E9E' }} />;
    }
    return null;
  };

  const timeFilters = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'all-time', label: 'All Time' },
  ];

  const renderTableHeaders = () => {
    const commonHeaders = ['Rank', 'Trader'];
    
    switch (selectedCategory) {
      case 0: // XM Points - Real data
        return [...commonHeaders, 'Gold', 'BTC (×2)', 'Silver (×3)', 'Total Points', 'Total Trades'];
      case 1: // XM Percentage
        return [...commonHeaders, 'Growth %'];
      case 2: // Weltrade Lots
        return [...commonHeaders, 'Lots Traded'];
      case 3: // Monthly Excellence
        return [...commonHeaders, 'Overall Score', 'Points Rank', '% Rank'];
      default:
        return [...commonHeaders, 'Gold', 'BTC (×2)', 'Silver (×3)', 'Total Points', 'Total Trades'];
    }
  };

  const renderTableCells = (trader) => {
    switch (selectedCategory) {
      case 0: // XM Points - Real data
        return (
          <>
            <TableCell>
              <Typography variant="body1" color="warning.main">
                {trader.goldPoints}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({trader.goldLots} lots)
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body1" color="info.main">
                {trader.btcPoints}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({trader.btcLots} lots)
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body1" color="grey.600">
                {trader.silverPoints}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({trader.silverLots} lots)
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {trader.totalPoints}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body1">
                {trader.totalTrades}
              </Typography>
            </TableCell>
          </>
        );
      case 1: // XM Percentage
        return (
          <>
            <TableCell>
              <Typography variant="h6" fontWeight="bold" color="text.secondary">
                {trader.percentageGrowth}
              </Typography>
            </TableCell>
          </>
        );
      case 2: // Weltrade Lots
        return (
          <>
            <TableCell>
              <Typography variant="h6" fontWeight="bold" color="text.secondary">
                {trader.lotsTraded}
              </Typography>
            </TableCell>
          </>
        );
      case 3: // Monthly Excellence
        return (
          <>
            <TableCell>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {trader.overallScore}/100
              </Typography>
            </TableCell>
            <TableCell>
              <Chip 
                label={`#${trader.pointsRank}`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </TableCell>
            <TableCell>
              <Chip 
                label={`#${trader.percentageRank}`} 
                size="small" 
                color="success" 
                variant="outlined"
              />
            </TableCell>
          </>
        );
      default:
        return null;
    }
  };

  // Mobile-friendly card layout
  const renderMobileCard = (trader, index) => {
    return (
      <motion.div
        key={selectedCategory === 0 ? trader.loginId : trader.rank}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <Card 
          sx={{ 
            mb: 2, 
            p: 2,
            background: trader.rank <= 3 ? `linear-gradient(135deg, ${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}15 0%, ${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}05 100%)` : 'inherit',
            border: trader.rank <= 3 ? `2px solid ${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}40` : '1px solid',
            borderColor: trader.rank <= 3 ? `${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}40` : 'divider',
          }}
        >
          {/* Header with rank and trader info */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                #{trader.rank}
              </Typography>
              {getBadgeIcon(trader.rank)}
            </Stack>
            
            <Avatar
              sx={{
                bgcolor: selectedCategory === 0 
                  ? (trader.hasUserProfile ? categories[selectedCategory].color : '#424242')
                  : categories[selectedCategory].color,
                border: trader.rank <= 3 ? `2px solid ${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}` : 'none',
                width: 48,
                height: 48,
              }}
            >
              {selectedCategory === 0 ? (
                trader.hasUserProfile ? 
                  trader.displayName.split(' ').map(n => n[0]).join('').toUpperCase() : 
                  <Person />
              ) : (
                trader.name?.split(' ').map(n => n[0]).join('') || '?'
              )}
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="h6" fontWeight="bold" noWrap>
                  {selectedCategory === 0 ? trader.displayName : trader.name}
                </Typography>
                {selectedCategory === 0 && trader.hasUserProfile && (
                  <Chip label="Registered" size="small" color="info" variant="outlined" />
                )}
                {trader.isVerified && selectedCategory !== 0 && (
                  <Chip label="Verified" size="small" color="success" variant="outlined" />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {selectedCategory === 0 ? `Acc Nr: ${trader.loginId}` : trader.country}
                {selectedCategory === 3 && trader.month && ` • ${trader.month}`}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Performance data based on category */}
          {selectedCategory === 0 && ( // XM Points
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="warning.main" fontWeight="bold">
                    {trader.goldPoints}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Gold ({trader.goldLots} lots)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="info.main" fontWeight="bold">
                    {trader.btcPoints}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    BTC ({trader.btcLots} lots)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="grey.600" fontWeight="bold">
                    {trader.silverPoints}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Silver ({trader.silverLots} lots)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {trader.totalPoints}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Points
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box textAlign="center" sx={{ mt: 1 }}>
                  <Typography variant="body1" color="text.secondary">
                    {trader.totalTrades} trades
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {selectedCategory === 1 && ( // XM Percentage
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="text.secondary">
                {trader.percentageGrowth}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Growth Percentage
              </Typography>
            </Box>
          )}

          {selectedCategory === 2 && ( // Weltrade
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="text.secondary">
                {trader.lotsTraded}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lots Traded
              </Typography>
            </Box>
          )}

          {selectedCategory === 3 && ( // Monthly Excellence
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h5" color="warning.main" fontWeight="bold">
                    {trader.overallScore}/100
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Overall Score
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Chip label={`#${trader.pointsRank}`} size="small" color="primary" variant="outlined" />
                    <Chip label={`#${trader.percentageRank}`} size="small" color="success" variant="outlined" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Points & % Ranks
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </Card>
      </motion.div>
    );
  };

  // Show loading state
  if (loading && selectedCategory === 0) {
    return (
      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={60} />
              <Typography variant="h6" color="text.secondary">
                Loading trader statistics...
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h2" gutterBottom sx={{ color: 'primary.main' }}>
              🏆 Competition Leaderboards
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Track performance across all FX Wars competition categories
            </Typography>
          </Box>
        </motion.div>

        {/* Error Alert */}
        {error && selectedCategory === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Alert severity="error" sx={{ mb: 4 }}>
              <AlertTitle>Error Loading Data</AlertTitle>
              {error}
              <Button startIcon={<RefreshIcon />} onClick={refetch} sx={{ mt: 1 }}>
                Retry
              </Button>
            </Alert>
          </motion.div>
        )}

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Paper elevation={2} sx={{ mb: 4, borderRadius: 3 }}>
            <Tabs
              value={selectedCategory}
              onChange={(e, newValue) => setSelectedCategory(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: isMobile ? 60 : 80,
                  py: isMobile ? 1 : 2,
                  px: isMobile ? 1.5 : 3,
                  minWidth: isMobile ? 'auto' : '120px',
                },
              }}
            >
              {categories.map((category, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      {isMobile ? (
                        // Mobile: Simplified layout
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                            <Box sx={{ color: category.color, fontSize: 20 }}>
                              {category.icon}
                            </Box>
                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                              {category.label.replace('XM Global - ', '').replace('Monthly Excellence Award', 'Excellence')}
                            </Typography>
                          </Stack>
                        </Box>
                      ) : (
                        // Desktop: Full layout
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Box sx={{ color: category.color }}>
                              {category.icon}
                            </Box>
                            <Typography variant="body1" fontWeight="bold">
                              {category.label}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {category.description}
                          </Typography>
                          <br />
                          {category.prize && (
                            <Chip
                              label={category.prize}
                              size="small"
                              sx={{
                                backgroundColor: `${category.color}20`,
                                color: category.color,
                                fontWeight: 'bold',
                                mt: 0.5,
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>
        </motion.div>

        {/* Time Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 4 }}>
            {/* Refresh button for live data */}
            {selectedCategory === 0 && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refetch}
                disabled={loading}
                sx={{ mb: 1 }}
              >
                Refresh
              </Button>
            )}
          </Stack>
        </motion.div>

        {/* Current Category Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Card sx={{ 
            mb: 4, 
            background: `linear-gradient(135deg, ${categories[selectedCategory].color}20 0%, ${categories[selectedCategory].color}10 100%)`,
            border: `2px solid ${categories[selectedCategory].color}40`
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack 
                direction={isMobile ? "column" : "row"} 
                alignItems={isMobile ? "flex-start" : "center"} 
                spacing={2} 
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ color: categories[selectedCategory].color, fontSize: isMobile ? 32 : 40 }}>
                    {categories[selectedCategory].icon}
                  </Box>
                  <Box>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      fontWeight="bold" 
                      color={categories[selectedCategory].color}
                    >
                      {categories[selectedCategory].label}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {categories[selectedCategory].description}
                    </Typography>
                    {selectedCategory === 0 && (
                      <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontWeight: 'medium' }}>
                        🏆 Only the Top 10 is eligible for a prize
                      </Typography>
                    )}
                  </Box>
                </Stack>
                {selectedCategory === 0 && lastUpdated && (
                  <Box textAlign={isMobile ? "left" : "right"} sx={{ mt: isMobile ? 2 : 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {lastUpdated.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </motion.div>

        {/* Main Leaderboard Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Card sx={{ overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 0 }}>
              {getCurrentData().length === 0 && selectedCategory === 0 ? (
                <Box sx={{ p: 8, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No trading data available yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trading data will appear here once traders start participating in the competition.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Mobile Card Layout */}
                  {isMobile ? (
                    <Box sx={{ p: 2 }}>
                      {getCurrentData().map((trader, index) => renderMobileCard(trader, index))}
                    </Box>
                  ) : (
                    /* Desktop Table Layout */
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: { xs: 800, md: 'auto' } }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: categories[selectedCategory].color }}>
                            {renderTableHeaders().map((header, index) => (
                              <TableCell key={index} sx={{ color: 'white', fontWeight: 'bold' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getCurrentData().map((trader, index) => (
                            <TableRow
                              key={selectedCategory === 0 ? trader.loginId : trader.rank}
                              sx={{
                                '&:hover': { backgroundColor: 'action.hover' },
                                backgroundColor: trader.rank <= 3 ? `${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}10` : 'inherit',
                              }}
                            >
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="h6" fontWeight="bold" sx={{ minWidth: '36px' }}>
                                    #{trader.rank}
                                  </Typography>
                                  {getBadgeIcon(trader.rank)}
                                </Stack>
                              </TableCell>
                              
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  {selectedCategory === 0 && (
                                    <Avatar
                                      sx={{
                                        bgcolor: trader.hasUserProfile ? categories[selectedCategory].color : '#424242',
                                        border: trader.rank <= 3 ? `2px solid ${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}` : 'none',
                                      }}
                                    >
                                      {trader.hasUserProfile ? 
                                        trader.displayName.split(' ').map(n => n[0]).join('').toUpperCase() : 
                                        <Person />
                                      }
                                    </Avatar>
                                  )}
                                  {selectedCategory !== 0 && (
                                    <Avatar
                                      sx={{
                                        bgcolor: categories[selectedCategory].color,
                                        border: trader.rank <= 3 ? `2px solid ${getBadgeColor(trader.rank === 1 ? 'gold' : trader.rank === 2 ? 'silver' : 'bronze')}` : 'none',
                                      }}
                                    >
                                      {trader.name?.split(' ').map(n => n[0]).join('') || '?'}
                                    </Avatar>
                                  )}
                                  <Box>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Typography variant="body1" fontWeight="medium">
                                        {selectedCategory === 0 ? trader.displayName : trader.name}
                                      </Typography>
                                      {selectedCategory === 0 && trader.hasUserProfile && (
                                        <Chip
                                          label="Registered"
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                        />
                                      )}
                                      {trader.isVerified && selectedCategory !== 0 && (
                                        <Chip
                                          label="Verified"
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                      {selectedCategory === 0 ? `Acc Nr: ${trader.loginId}` : trader.country}
                                      {selectedCategory === 3 && trader.month && ` • ${trader.month}`}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              
                              {renderTableCells(trader)}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer Note */}
        <Box sx={{ mt: 4, textAlign: 'center', px: isMobile ? 2 : 0 }}>
          <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
            * Rankings are updated in real-time. All data is calculated from the competition start date.
          </Typography>
          <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ mt: 1 }}>
            🔄 Last updated: {selectedCategory === 0 && lastUpdated ? lastUpdated.toLocaleString() : new Date().toLocaleString()}
          </Typography>
        </Box>

        {/* Small Competition Timeline */}
        <Box sx={{ mt: 4, mb: 1, textAlign: 'center', px: isMobile ? 2 : 0 }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
          >
            📅 Competition runs from <strong>June 1, 2025</strong> to <strong>December 31, 2025</strong> (7 months)
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LeaderboardPage; 